import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CountdownFloat from '@/components/base/CountdownFloat';
import { useAuth, api } from '@/lib/api';
import { useFamilyData } from '@/lib/context/FamilyDataContext';
import { useCountdown } from '@/lib/context/CountdownContext';

const navItems = [
  { path: '/dashboard', icon: 'ri-home-5-line', activeIcon: 'ri-home-5-fill', label: '首页' },
  { path: '/members', icon: 'ri-group-line', activeIcon: 'ri-group-fill', label: '成员管理' },
  { path: '/rewards', icon: 'ri-medal-line', activeIcon: 'ri-medal-fill', label: '奖惩机制' },
  { path: '/analytics', icon: 'ri-bar-chart-2-line', activeIcon: 'ri-bar-chart-2-fill', label: '习惯分析' },
  { path: '/bank', icon: 'ri-bank-line', activeIcon: 'ri-bank-fill', label: '家庭银行' },
  { path: '/ai', icon: 'ri-robot-2-line', activeIcon: 'ri-robot-2-fill', label: 'AI助手' },
  { path: '/market', icon: 'ri-store-3-line', activeIcon: 'ri-store-3-fill', label: '激励市场' }
];

// 父母可管理家庭；其他亲属（舅舅、叔叔等）只能记录，无管理权
const familyList = [
  { id: 'f1', name: '张家', relation: '父亲', children: 2, adults: 4, isVip: true, canManage: true },
  { id: 'f2', name: '李家（姐姐家）', relation: '舅舅', children: 1, adults: 3, isVip: true, canManage: false },
  { id: 'f3', name: '王家（弟弟家）', relation: '叔叔', children: 2, adults: 2, isVip: false, canManage: false }
];

interface Props {
  children: React.ReactNode;
  onRefresh?: () => void;
}

export default function DashboardLayout({ children, onRefresh }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, families, currentFamily, logout, setCurrentFamily, refreshFamilies } = useAuth();
  const { childrenMap } = useFamilyData(); // 从共享Context获取childrenMap
  const [collapsed, setCollapsed] = useState(false);
  const [showFamilySwitcher, setShowFamilySwitcher] = useState(false);
  const [showCreateFamily, setShowCreateFamily] = useState(false);
  const [newFamilyName, setNewFamilyName] = useState('');
  const [createFamilyLoading, setCreateFamilyLoading] = useState(false);
  const [createFamilyError, setCreateFamilyError] = useState('');
  const { countdownItems, removeCountdown: ctxRemoveCountdown, finishCountdown: ctxFinishCountdown } = useCountdown();

  // 计时任务由各页面自行加载，DashboardLayout只负责显示

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // 从认证上下文获取活跃家庭
  const activeFamilyId = currentFamily?.id || '';
  const familyList = families.map(f => ({
    id: f.id,
    name: f.name,
    relation: f.my_identity || '其他成员',
    children: f.child_count || 0,
    adults: f.member_count || 0,
    isVip: f.vip_status === 2,
    canManage: f.my_role === 1
  }));

  const isActive = (path: string) => location.pathname === path;
  const activeFamily = familyList.find(f => f.id === activeFamilyId) || familyList[0] || {
    id: '',
    name: '加载中...',
    relation: '',
    children: 0,
    adults: 0,
    isVip: false,
    canManage: false
  };

  // 检查记录员权限
  const canManage = activeFamily.canManage;

  const handleFamilySwitch = (family: typeof familyList[0]) => {
    const fullFamily = families.find(f => f.id === family.id);
    if (fullFamily) {
      setCurrentFamily(fullFamily);
      setShowFamilySwitcher(false);
      // 清除页面缓存，确保切换家庭后各页面重新加载数据
      localStorage.removeItem('family_data_cache');
      localStorage.removeItem('dashboard_data_cache');
      localStorage.removeItem('rewards_cache');
      // 切换家庭后跳转到Dashboard，避免其他页面数据加载失败
      navigate('/dashboard');
    }
  };

  // 监听Dashboard页面的切换家庭事件
  useEffect(() => {
    const handler = () => setShowFamilySwitcher(true);
    window.addEventListener('open-family-switcher', handler);
    return () => window.removeEventListener('open-family-switcher', handler);
  }, []);

  const handleCreateFamily = async () => {
    if (!newFamilyName.trim()) {
      setCreateFamilyError('请输入家庭名称');
      return;
    }
    setCreateFamilyLoading(true);
    setCreateFamilyError('');
    try {
      const res = await api.family.createFamily({ name: newFamilyName.trim() });
      if (res.code === 0 && res.data) {
        // 刷新家庭列表
        await refreshFamilies();
        setShowCreateFamily(false);
        setShowFamilySwitcher(false);
        setNewFamilyName('');
      } else {
        setCreateFamilyError(res.message || '创建失败');
      }
    } catch (e: any) {
      setCreateFamilyError(e?.message || '创建失败');
    } finally {
      setCreateFamilyLoading(false);
    }
  };

  const handleRemoveCountdown = async (id: string) => {
    // 查找任务判断是否已完成
    const item = countdownItems.find(i => i.id === id);
    if (item) {
      const elapsed = Math.floor((Date.now() - item.startedAt) / 1000);
      const remaining = Math.max(0, item.totalSeconds - elapsed);

      // 只有计时已完成时才更新数据库状态
      if (remaining <= 0) {
        try {
          await api.timer.updateTask(id, { remaining_seconds: 0, status: 2 });
        } catch (error) {
          console.error('Failed to update timer task:', error);
        }
      }
    }
    ctxRemoveCountdown(id);
  };

  const handleFinishCountdown = async (id: string) => {
    // 播放音乐提醒 - 需要用户交互后才能播放
    try {
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioContextClass) {
        const audioCtx = new AudioContextClass();
        if (audioCtx.state === 'suspended') {
          audioCtx.resume();
        }
        const osc = audioCtx.createOscillator();
        const gain = audioCtx.createGain();
        osc.connect(gain);
        gain.connect(audioCtx.destination);
        osc.frequency.value = 880;
        gain.gain.setValueAtTime(0.3, audioCtx.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001, audioCtx.currentTime + 2);
        osc.start(audioCtx.currentTime);
        osc.stop(audioCtx.currentTime + 2);
      }
    } catch (e) {
      console.debug('Audio playback not available:', e);
    }

    // 更新任务状态为已完成（status=2）
    try {
      await api.timer.updateTask(id, { remaining_seconds: 0, status: 2 });
    } catch (error) {
      console.error('Failed to update timer task:', error);
    }

    ctxFinishCountdown(id);
  };

  const activeCountdowns = countdownItems.filter(item => {
    const elapsed = Math.floor((Date.now() - item.startedAt) / 1000);
    return item.totalSeconds - elapsed > 0;
  });

  return (
    <div className="flex h-screen bg-[#F0F7FF] overflow-hidden">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex flex-col transition-all duration-300 ${collapsed ? 'w-14' : 'w-56'} flex-shrink-0`}
        style={{ background: 'linear-gradient(180deg, #1565C0 0%, #1976D2 50%, #1E88E5 100%)' }}
      >
        {/* Logo */}
        <div className={`flex items-center ${collapsed ? 'justify-center px-2' : 'justify-between px-4'} h-14 border-b border-white/10`}>
          {!collapsed && (
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-8 h-8 flex items-center justify-center rounded-xl bg-gradient-to-br from-yellow-400 to-amber-500 flex-shrink-0">
                <i className="ri-planet-fill text-white text-base"></i>
              </div>
              <span className="font-black text-white text-sm tracking-wide truncate">习惯星球</span>
            </div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-6 h-6 flex items-center justify-center text-white/40 hover:text-white hover:bg-white/10 rounded-lg transition-colors cursor-pointer flex-shrink-0"
          >
            <i className={`${collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'} text-sm`}></i>
          </button>
        </div>

        {/* Family Card */}
        {!collapsed && (
          <div className="px-3 py-2.5 border-b border-white/10">
            <button
              onClick={() => setShowFamilySwitcher(true)}
              className="w-full bg-white/10 hover:bg-white/15 rounded-xl p-3 transition-colors cursor-pointer text-left"
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-gradient-to-br from-yellow-400 to-amber-500 flex-shrink-0">
                  <i className="ri-home-heart-fill text-white text-sm"></i>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-white font-bold text-xs truncate">{activeFamily.name}</span>
                    {activeFamily.isVip && (
                      <span className="bg-gradient-to-r from-yellow-400 to-amber-500 text-white text-[8px] font-black px-1.5 py-0.5 rounded-full flex items-center gap-0.5 whitespace-nowrap flex-shrink-0">
                        <i className="ri-vip-crown-fill"></i> VIP
                      </span>
                    )}
                  </div>
                  <div className="text-white/50 text-[10px] mt-0.5">{activeFamily.relation} · {activeFamily.children}子女</div>
                </div>
                <i className="ri-arrow-down-s-line text-white/40 text-xs flex-shrink-0"></i>
              </div>
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 px-2 py-2.5 overflow-y-auto">
          {navItems.map(item => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl mb-0.5 transition-all cursor-pointer relative ${
                isActive(item.path)
                  ? 'bg-white/20 text-white'
                  : 'text-white/55 hover:bg-white/10 hover:text-white/85'
              } ${collapsed ? 'justify-center' : ''}`}
              title={collapsed ? item.label : ''}
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <i className={`${isActive(item.path) ? item.activeIcon : item.icon} text-base`}></i>
              </div>
              {!collapsed && (
                <>
                  <span className="text-xs font-medium whitespace-nowrap flex-1 text-left">{item.label}</span>
                  {isActive(item.path) && (
                    <div className="w-1.5 h-1.5 rounded-full bg-yellow-300 flex-shrink-0"></div>
                  )}
                </>
              )}
              {!collapsed && isActive(item.path) && (
                <div className="absolute left-0 w-1 h-8 rounded-r-full bg-yellow-300"></div>
              )}
            </button>
          ))}
        </nav>

        {/* Bottom Actions */}
        <div className={`px-2 py-2.5 border-t border-white/10 ${collapsed ? 'flex flex-col items-center' : ''}`}>
          <button
            onClick={() => navigate('/settings')}
            className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer transition-colors ${
              isActive('/settings') ? 'bg-white/20 text-white' : 'text-white/45 hover:bg-white/10 hover:text-white/75'
            } ${collapsed ? 'justify-center' : ''}`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className={`${isActive('/settings') ? 'ri-settings-4-fill' : 'ri-settings-4-line'} text-base`}></i>
            </div>
            {!collapsed && <span className="text-xs font-medium">系统设置</span>}
          </button>
        </div>
      </aside>

      {/* Mobile bottom nav */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-50 flex items-center justify-around px-1 py-2"
        style={{ background: 'linear-gradient(90deg, #1565C0 0%, #1E88E5 100%)' }}
      >
        {navItems.slice(0, 5).map(item => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={`flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg transition-colors cursor-pointer ${isActive(item.path) ? 'text-yellow-300' : 'text-white/45'}`}
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`${isActive(item.path) ? item.activeIcon : item.icon} text-lg`}></i>
            </div>
            <span className="text-[9px] whitespace-nowrap">{item.label}</span>
          </button>
        ))}
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <main className="flex-1 overflow-y-auto pb-14 min-w-0">
          <div className="w-full">
            {children}
          </div>
        </main>
      </div>

      {/* Fixed bottom ad banner */}
      <div
        className="hidden md:flex fixed bottom-0 right-0 z-40 bg-white/95 backdrop-blur-sm border-t border-blue-100 items-center gap-4 px-5 py-2.5 transition-all duration-300"
        style={{ left: collapsed ? '56px' : '224px' }}
      >
        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-gradient-to-r from-blue-500 to-cyan-500 flex-shrink-0">
          <i className="ri-advertisement-fill text-white text-sm"></i>
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-xs text-blue-800 font-semibold">推荐好物 · </span>
          <span className="text-xs text-gray-500">儿童早教绘本套装，培养阅读好习惯 — 限时7折！升级VIP即可免广告体验</span>
        </div>
        <button className="text-xs text-white bg-gradient-to-r from-blue-500 to-cyan-500 px-3 py-1.5 rounded-lg whitespace-nowrap cursor-pointer flex-shrink-0 hover:opacity-90 transition-opacity">
          了解更多
        </button>
        <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100">广告</span>
      </div>

      {/* Countdown Float Panel */}
      {countdownItems.length > 0 && (
        <div className="fixed top-14 right-4 z-[100]">
          <CountdownFloat
            items={countdownItems}
            onRemove={handleRemoveCountdown}
            onFinish={handleFinishCountdown}
          />
        </div>
      )}

      {/* Family Switcher Modal */}
      {showFamilySwitcher && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowFamilySwitcher(false)}>
          <div className="bg-white rounded-2xl w-full max-w-[420px] overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-blue-50" style={{ background: 'linear-gradient(135deg, #1565C0, #1E88E5)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/20">
                    <i className="ri-home-heart-fill text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-sm">切换家庭</h3>
                    <p className="text-white/60 text-xs">选择要管理的家庭</p>
                  </div>
                </div>
                <button onClick={() => setShowFamilySwitcher(false)} className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white cursor-pointer rounded-lg hover:bg-white/10">
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
            <div className="p-4 space-y-2">
              <div className="bg-amber-50 border border-amber-100 rounded-xl px-3 py-2 mb-3">
                <p className="text-xs text-amber-700 flex items-start gap-1.5">
                  <i className="ri-information-fill text-amber-500 mt-0.5 flex-shrink-0"></i>
                  <span>只有<strong>父/母</strong>身份可管理家庭设置；其他亲属身份（舅舅、叔叔等）仅能参与记录，无管理权限。</span>
                </p>
              </div>
              {familyList.map(family => (
                <button
                  key={family.id}
                  onClick={() => handleFamilySwitch(family)}
                  className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 transition-all cursor-pointer text-left ${
                    activeFamilyId === family.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-100 hover:border-blue-200 bg-white'
                  }`}
                >
                  <div className={`w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0 ${
                    activeFamilyId === family.id ? 'bg-gradient-to-br from-blue-400 to-blue-600' : 'bg-blue-100'
                  }`}>
                    <i className={`ri-home-heart-fill text-xl ${activeFamilyId === family.id ? 'text-white' : 'text-blue-500'}`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                      <span className={`font-bold text-sm ${activeFamilyId === family.id ? 'text-blue-600' : 'text-gray-800'}`}>{family.name}</span>
                      {family.isVip && (
                        <span className="bg-gradient-to-r from-yellow-400 to-amber-500 text-white text-[9px] font-black px-1.5 py-0.5 rounded-full whitespace-nowrap">VIP</span>
                      )}
                      {activeFamilyId === family.id && (
                        <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full font-medium whitespace-nowrap">当前</span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                      <span className="text-xs text-gray-500">身份：<strong>{family.relation}</strong></span>
                      {family.canManage ? (
                        <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-100 whitespace-nowrap">
                          <i className="ri-shield-check-fill text-[9px]"></i>管理权限
                        </span>
                      ) : (
                        <span className="flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full bg-gray-50 text-gray-400 border border-gray-200 whitespace-nowrap">
                          <i className="ri-edit-line text-[9px]"></i>仅记录
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400">
                      <span><i className="ri-user-smile-fill mr-0.5"></i>{family.children}子女</span>
                      <span><i className="ri-parent-fill mr-0.5"></i>{family.adults}家长</span>
                    </div>
                  </div>
                  {activeFamilyId === family.id && (
                    <div className="w-6 h-6 flex items-center justify-center rounded-full bg-blue-500 flex-shrink-0">
                      <i className="ri-check-line text-white text-xs"></i>
                    </div>
                  )}
                </button>
              ))}
              <div className="pt-2 border-t border-blue-50">
                <button onClick={() => { setShowCreateFamily(true); setNewFamilyName(''); setCreateFamilyError(''); }}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border-2 border-dashed border-blue-200 text-blue-500 text-sm font-medium hover:bg-blue-50 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-add-line"></i>加入或创建新家庭
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Family Modal */}
      {showCreateFamily && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setShowCreateFamily(false)}>
          <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-blue-50" style={{ background: 'linear-gradient(135deg, #1565C0, #1E88E5)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white/20">
                    <i className="ri-home-heart-fill text-white text-lg"></i>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-sm">创建新家庭</h3>
                    <p className="text-white/60 text-xs">创建后自动切换到新家庭</p>
                  </div>
                </div>
                <button onClick={() => setShowCreateFamily(false)} className="w-8 h-8 flex items-center justify-center text-white/70 hover:text-white cursor-pointer rounded-lg hover:bg-white/10">
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-sm text-gray-600 mb-1.5 block">家庭名称</label>
                <input
                  value={newFamilyName}
                  onChange={e => { setNewFamilyName(e.target.value); setCreateFamilyError(''); }}
                  placeholder="例如：张家、我的家庭"
                  className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-blue-400 transition-colors"
                  onKeyDown={e => { if (e.key === 'Enter') handleCreateFamily(); }}
                  autoFocus
                />
              </div>
              {createFamilyError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                  {createFamilyError}
                </div>
              )}
              <div className="flex gap-3">
                <button onClick={() => setShowCreateFamily(false)}
                  className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-gray-600 hover:bg-gray-50 transition-all cursor-pointer whitespace-nowrap">
                  取消
                </button>
                <button onClick={handleCreateFamily} disabled={createFamilyLoading || !newFamilyName.trim()}
                  className="flex-1 py-2.5 rounded-xl text-white text-sm font-medium transition-all cursor-pointer whitespace-nowrap disabled:opacity-50"
                  style={{ background: 'linear-gradient(135deg, #1565C0, #1E88E5)' }}>
                  {createFamilyLoading ? '创建中...' : '创建家庭'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
